import React from 'react';
import { Box, Typography, Stack, Link, useTheme, alpha } from '@mui/material';
import { ErrorOutlineIcon, EmailIcon, PhoneIcon, OpenInNewIcon } from '@/components/icons';
import { FALLBACK_SERVICE_MESSAGE } from '../utils/fallback';
import { useWidgetBrandColors } from './useWidgetBrandColors';

export type ServiceUnavailableContentProps = {
  message?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  supportLinkUrl?: string | null;
  supportLinkLabel?: string | null;
  /** Move focus to the heading on mount. Off in the embedded panel. Default true. */
  autoFocusHeading?: boolean;
};

/**
 * ServiceUnavailableContent - error content shown when AI chat is unavailable
 * This is just the content area - the header and footer are handled by ChatDrawer
 */
/**
 * Validates and returns the URL only if it's a valid HTTPS URL.
 * Returns null for invalid URLs, non-HTTPS schemes (including javascript:), or empty values.
 */
function validateHttpsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

export function ServiceUnavailableContent({
  message,
  supportEmail,
  supportPhone,
  supportLinkUrl,
  supportLinkLabel,
  autoFocusHeading = true,
}: ServiceUnavailableContentProps) {
  const theme = useTheme();
  const { primaryOnPaper } = useWidgetBrandColors();
  const displayMessage = message || FALLBACK_SERVICE_MESSAGE;
  const headingId = React.useId();
  const messageId = React.useId();
  const headingRef = React.useRef<HTMLHeadingElement | null>(null);

  // Announcing the outage through a live region proved unreliable: VoiceOver
  // routinely ignores `aria-live` inside a shadow root, and nothing here is
  // focusable enough for Tab to reach the explanation on its own. Moving focus
  // to the heading makes the screen reader read the outage the moment it
  // appears. The microtask defers past the panel's focus trap, which claims
  // focus in its own mount effect.
  React.useEffect(() => {
    if (!autoFocusHeading) return;
    queueMicrotask(() => headingRef.current?.focus());
  }, [autoFocusHeading]);

  // Validate and derive support link values - only show if both URL and label are valid
  // URL must be HTTPS to prevent injection/phishing attacks
  const validatedLinkUrl = validateHttpsUrl(supportLinkUrl);
  const validatedLinkLabel = supportLinkLabel?.trim() || null;
  const hasSupportLink = validatedLinkUrl !== null && validatedLinkLabel !== null;

  const hasSupport = supportEmail || supportPhone || hasSupportLink;

  return (
    <Stack
      role="region"
      aria-labelledby={headingId}
      sx={{
        flex: 1,
        p: 4,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
      spacing={3}
    >
      {/* Warning icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.warning.light, 0.3),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 40, color: 'warning.main' }} aria-hidden />
      </Box>

      {/* Heading. Described by the message below so focusing it reads the
          whole explanation, not just the two-word title. */}
      <Typography
        id={headingId}
        ref={headingRef}
        component="h2"
        variant="h5"
        fontWeight={600}
        tabIndex={-1}
        aria-describedby={messageId}
        // Programmatic focus only; this is not a keyboard-operable control, so
        // a focus ring here would read as a rendering bug.
        sx={{ outline: 'none' }}
      >
        Service Unavailable
      </Typography>

      {/* Message */}
      <Typography id={messageId} variant="body1" color="text.secondary" sx={{ maxWidth: 320 }}>
        {displayMessage}
      </Typography>

      {/* Support section */}
      {hasSupport && (
        <Stack spacing={2} sx={{ mt: 2, width: '100%', maxWidth: 280 }}>
          <Typography
            component="p"
            variant="subtitle2"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Get Support Now
          </Typography>

          {/* Support Link - displayed first per mockup order: link, email, phone */}
          {hasSupportLink && (
            <Link
              href={validatedLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                color: primaryOnPaper,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <OpenInNewIcon fontSize="small" aria-hidden />
              <Typography variant="body2">{validatedLinkLabel}</Typography>
            </Link>
          )}

          {supportEmail && (
            <Link
              href={`mailto:${supportEmail}`}
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                color: primaryOnPaper,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <EmailIcon fontSize="small" aria-hidden />
              <Typography variant="body2">{supportEmail}</Typography>
            </Link>
          )}

          {supportPhone && (
            <Link
              href={`tel:${supportPhone}`}
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                color: primaryOnPaper,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <PhoneIcon fontSize="small" aria-hidden />
              <Typography variant="body2">{supportPhone}</Typography>
            </Link>
          )}
        </Stack>
      )}
    </Stack>
  );
}
